import { Component, Input, OnDestroy } from "@angular/core";
import { combineLatest, Subject, Subscription, takeUntil } from "rxjs";
import { GasprecioService } from "../../../gasprecio/services/gasprecio.service";
import { SharedService } from "../../../gasprecio/services/shared.service";
import {
  Router,
  Event as NavigationEvent,
  NavigationEnd,
} from "@angular/router";
import Highcharts from "highcharts";
import { GroupIds } from "src/app/modules/gasprecio/enums/groups-ids.enum";
import moment from "moment";
import type {
  Chart as HighchartsChart,
  ChartCallbackFunction as HighchartsChartCallback
} from 'highcharts';

@Component({
  selector: "app-scorecard-by-product",
  templateUrl: "./scorecard-by-product.component.html",
  styleUrls: ["./scorecard-by-product.component.scss"],
})
export class ScorecardByProductComponent implements OnDestroy {

  @Input() product: any = "regular";
  @Input() station: any = false;

  stationSelected$!: Subscription;
  stationSelected!: string;

  chartOptions: Highcharts.Options;
  private chartRefLiters: HighchartsChart | null = null;
  private chartRefCount:  HighchartsChart | null = null;
  private chartRefAmount: HighchartsChart | null = null;
  chartCallbackLiters: HighchartsChartCallback = (c) => { this.chartRefLiters = c; };
  chartCallbackCount:  HighchartsChartCallback = (c) => { this.chartRefCount  = c; };
  chartCallbackAmount: HighchartsChartCallback = (c) => { this.chartRefAmount = c; };
  data = [];
  date = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
  event$!: Subscription;
  Highcharts = Highcharts;
  hours = [];
  idGroup: GroupIds | string = GroupIds.Pa;
  loading = false;
  private destroy$ = new Subject<void>();
  period = "day";
  reqst = 0;
  rol;
  route;
  stationId: any;
  zone;
  total;
  changeZone = false;
  chartOptionsLiters: Highcharts.Options;
  chartOptionsCount: Highcharts.Options;
  chartOptionsAmount: Highcharts.Options;

  constructor(
    private gasprecioService: GasprecioService,
    private sharedService: SharedService,
    private router: Router
  ) {
    this.rol = localStorage.getItem("rol");
    this.idGroup = this.rol === "Dios" ? localStorage.getItem("group") || GroupIds.Pa : localStorage.getItem("group");
    this.event$ = this.router.events.subscribe((event: NavigationEvent) => {
      if (event instanceof NavigationEnd) {
        this.route = /[^/]*$/.exec(event.url)[0];
      }
    });

    this.stationSelected$ = this.sharedService.sharedStation.subscribe(stationSelected => {
      if (!this.station && stationSelected !== '') {
        this.stationSelected = stationSelected;
      }
    });

    combineLatest([
      this.sharedService.sharedGroup,
      this.sharedService.sharedZoneHeatMap,
      this.sharedService.sharedStationSelected,
      this.sharedService.sharedSelectedMonth,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([idGroup, zone, stationId, period]) => {
        if (idGroup !== this.idGroup) {
          this.idGroup = idGroup;
        }

        if (this.zone !== zone) {
          if (this.zone !== undefined) {
            this.changeZone = true;
          }
          this.zone = zone;
        }

        if (this.stationId !== stationId) {
          this.stationId = stationId;
        }

        if (period !== this.date) {
          this.date = period;
        }

        if (!this.loading) {
          this.getHeatMapScorecard();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stationSelected$.unsubscribe();
  }

  getHeatMapScorecard() {
    this.loading = true;
    this.reqst = 0;
    this.data = [];
    this.hours = [];

    this.stationSelected$ = this.sharedService.sharedStation.subscribe(stationSelected => {
      if (!this.station && stationSelected !== '') {
        this.stationSelected = stationSelected;
      }
    });

    setTimeout(() => {
      const stationCondition = this.station || (this.stationSelected$ && this.stationSelected !== 'zone');
      let url: any;

      if (stationCondition) {
        url = this.gasprecioService.getHeatMapScorecardByStation(this.idGroup, this.product, this.date, this.stationSelected);
      } else if (this.zone === '' || !this.zone) {
        url = this.gasprecioService.getHeatMapScorecard(this.idGroup, this.stationId, this.product, this.date, false);
      } else {
        url = this.gasprecioService.getHeatMapScorecard(this.idGroup, this.zone, this.product, this.date, true);
      }

      url.subscribe({
        next: (response: any) => {
          this.total = response.total;
          const litersData = [
            response.small.liters || 0,
            response.medium.liters || 0,
            response.large.liters || 0,
            response.longLarge.liters || 0
          ];
          const countData = [
            response.small.count || 0,
            response.medium.count || 0,
            response.large.count || 0,
            response.longLarge.count || 0
          ];
          const amountData = [
            response.small.amount || 0,
            response.medium.amount || 0,
            response.large.amount || 0,
            response.longLarge.amount || 0
          ];
          this.setChartData(litersData, countData, amountData);
        },
        complete: () => {
          this.reqst = 1;
        },
        error: () => {
          this.loading = false;
          this.reqst = 2;
        },
      });
    }, 2000);
  }

  setChartData(litersData: number[], countData: number[], amountData: number[]) {
    const colorP = this.product === 'regular' ? '#159546' : this.product === 'premium' ? '#ea0000' : this.product === 'diesel' ? '#1e2228' : '';
    const colorT = this.product === 'regular' ? 'black' : this.product === 'premium' ? 'black' : this.product === 'diesel' ? 'white' : '';

    const commonOptions = {
      chart: {
        type: "bar",
        height: "250px",
      },
      xAxis: {
        categories: [
          "CHICA",
          "REGULAR",
          "GRANDE",
          "MUY GRANDE"
        ],
        labels: {
          enabled: false,
        },
        gridLineWidth: 0
      },
      yAxis: {
        min: 0,
        title: {
          text: null,
        },
        labels: {
          enabled: false,
        },
        gridLineColor: "transparent"
      },
      plotOptions: {
        bar: {
          dataLabels: {
            enabled: true,
            inside: true,
            formatter: function () {
              return this.y ? this.y.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '';
            },
            align: 'left',
            verticalAlign: 'middle',
            y: -1,
            style: {
              color: colorT,
              fontSize: '13px',
              fontWeight: 'bold'
            }
          },
        }
      },
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'
      },
      exporting: {
        enabled: false
      }
    };

    this.chartOptionsLiters = {
      ...commonOptions,
      title: { text: "Litros", style: { fontSize: '0' } },
      xAxis: {
        ...commonOptions.xAxis
      },
      series: [{ name: "Litros", type: "bar", data: litersData, color: colorP, }]
    };

    this.chartOptionsCount = {
      ...commonOptions,
      title: { text: "Autos", style: { fontSize: '0' } },
      series: [{ name: "Autos", type: "bar", data: countData, color: colorP, }],
    };

    this.chartOptionsAmount = {
      ...commonOptions,
      title: { text: "Ingreso", style: { fontSize: '0' } },
      series: [{ name: "Ingreso", type: "bar", data: amountData, color: colorP, }],
      tooltip: {
        pointFormat: '{series.name}: <b>$ {point.y:,.0f}</b>'
      },
      plotOptions: {
        bar: {
          dataLabels: {
            enabled: true,
            inside: true,
            formatter: function () {
              return this.y ? '$ ' + this.y.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '';
            },
            align: 'left',
            verticalAlign: 'middle',
            style: {
              color: colorT,
              fontSize: '13px',
              fontWeight: 'bold'
            }
          },
        }
      }
    };

    this.loading = false;
  }

  private async chartToPng(chart: HighchartsChart, scale = 2,fallbackOptions?: Highcharts.Options): Promise<string> {
    const w = 170, h = 340;
    const exportOverrides: Highcharts.Options = {
      plotOptions: {
        series: {
          dataLabels: {
            style: { fontSize: '24px', fontWeight: '600' }
          }
        }
      }
    };

    let svg: string;

    try {
      if (!chart || (chart as any).destroyed) throw new Error('dead');
      const opts = Highcharts.merge(
        { exporting: { sourceWidth: w, sourceHeight: h, scale } },
        exportOverrides
      ) as Highcharts.Options;
      svg = chart.getSVG(opts);

    } catch {
      if (!fallbackOptions) throw new Error('Chart no inicializado y sin fallbackOptions');

      const el = document.createElement('div');
      el.style.position = 'absolute';
      el.style.left = '-99999px';
      el.style.width = `${w}px`;
      el.style.height = `${h}px`;
      document.body.appendChild(el);

      try {
        const tmpOpts = Highcharts.merge(
          {},
          fallbackOptions,
          exportOverrides,
          { chart: { width: w, height: h, animation: false },
            exporting: { sourceWidth: w, sourceHeight: h, scale },
            credits: { enabled: false } } as Highcharts.Options
        );
        const tmp = Highcharts.chart(el, tmpOpts);
        svg = tmp.getSVG({ exporting: { sourceWidth: w, sourceHeight: h, scale } });
        tmp.destroy();
      } finally {
        el.remove();
      }
    }
    const svgUrl = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = rej; img.src = svgUrl; });
    const canvas = document.createElement('canvas');
    canvas.width = img.width || 1200 * scale;
    canvas.height = img.height || 600 * scale;
    canvas.getContext('2d')!.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  }
  async toDataURLLiters(scale = 2) {
    return this.chartToPng(this.chartRefLiters, scale, this.chartOptionsLiters);
  }
  async toDataURLCount(scale = 2) {
    return this.chartToPng(this.chartRefCount, scale, this.chartOptionsCount);
  }
  async toDataURLAmount(scale = 2) {
    return this.chartToPng(this.chartRefAmount, scale, this.chartOptionsAmount);
  }

  async toDataURLAll(scale = 2) {
    const [liters, count, amount] = await Promise.all([
      this.toDataURLLiters(scale),
      this.toDataURLCount(scale),
      this.toDataURLAmount(scale),
    ]);
    return { liters, count, amount };
  }
}
